import { useEffect, useState } from 'react';
import axios from 'axios';
import { Leaf, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FIRST_SHOW_MS = 6000;       // Wait 6s after page load
const INTERVAL_MS = 30000;        // Show one every 30s
const VISIBLE_MS = 4500;          // Each toast visible 4.5s

export default function RecentBookingsNotifier() {
  const [bookings, setBookings] = useState([]);
  const [current, setCurrent] = useState(null);
  const [closed, setClosed] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/site-pulse`).then(r => {
      const list = (r.data?.recent_bookings || []).filter(b => b.days_ago != null);
      setBookings(list);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (closed || !bookings.length) return undefined;
    let cancelled = false;
    let idx = 0;
    const show = () => {
      if (cancelled) return;
      setCurrent({ ...bookings[idx], key: Date.now() });
      setTimeout(() => {
        if (!cancelled) setCurrent(null);
      }, VISIBLE_MS);
      idx = (idx + 1) % bookings.length;
    };
    const firstId = setTimeout(() => {
      show();
      const intId = setInterval(show, INTERVAL_MS);
      // store interval id on closure
      cancelled = false;
      return () => clearInterval(intId);
    }, FIRST_SHOW_MS);
    return () => {
      cancelled = true;
      clearTimeout(firstId);
    };
  }, [bookings, closed]);

  if (!current || closed) return null;

  const ago = current.days_ago === 0
    ? "aujourd'hui"
    : current.days_ago === 1
      ? 'hier'
      : `il y a ${current.days_ago} jours`;

  return (
    <div
      key={current.key}
      data-testid="recent-booking-toast"
      className="fixed bottom-6 left-6 z-40 max-w-[320px] bg-white/85 backdrop-blur-xl border border-black/5 shadow-[0_20px_60px_-12px_rgba(26,28,25,0.18)] rounded-2xl p-4 pr-10"
      style={{ animation: 'pulse-slide-in 0.6s cubic-bezier(0.16, 1, 0.3, 1)' }}
    >
      <button
        type="button"
        onClick={() => setClosed(true)}
        aria-label="Fermer"
        data-testid="recent-booking-close"
        className="absolute top-2.5 right-2.5 text-[#1A1C19]/35 hover:text-[#1A1C19] transition-colors"
      >
        <X className="w-3.5 h-3.5" strokeWidth={2} />
      </button>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-[#CBA77A]/15 flex items-center justify-center flex-shrink-0">
          <Leaf className="w-4 h-4 text-[#CBA77A]" strokeWidth={1.8} />
        </div>
        <div className="text-[12px] leading-snug">
          <p className="text-[#1A1C19] font-medium">
            {current.initial_name} a reserve {current.accommodation_label === 'Gite + Salle' ? 'le Gite + Salle' : current.accommodation_label.startsWith('Apt') ? `l'${current.accommodation_label}` : 'le Gite'}
          </p>
          <p className="text-[#5A6153] mt-0.5">
            {ago}
            {current.nights ? ` · ${current.nights} nuit${current.nights > 1 ? 's' : ''}` : ''}
          </p>
        </div>
      </div>
      <style>{`
        @keyframes pulse-slide-in {
          0%   { opacity: 0; transform: translateY(12px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
